export const Settings = {};


/**
 * @constant
 * @type {Object}
 */
Settings.time = {
    /**
     * @constant
     * @type {number}
     */
    defaultFramesPerSecond: 60,
    /**
     * @constant
     * @type {number}
     */
    defaultMillisPerFrame : 1000 / 60, // <-- millis per second / default frame per second
    /**
     * @constant
     * @type {number}
     */
    turnsPerSecond: 4,
    /**
     * @constant
     * @type {number}
     */
    secondsPerTurn: 0.25,  // 1 seconds / 4 turns per second
    /**
     * @constant
     * @type {number}
     */
    millisPerTurn: 250,  // 1000 / 4
    /**
     * @constant
     * @type {number}
     */
    turnFrames: 15   // 60 FPS / 4 turns per second.
};

/**
 * @constant
 * @type {Object}
 */
Settings.target = {
    minTargetRow: 5
}

/**
 * @constant
 * @type {Object}
 */
Settings.damage = {
    baseDamagePerSecond: 30,
    baseDamagePerTurn: 30 * Settings.time.secondsPerTurn
}


/**
 * @constant
 * @type {Object}
 */
Settings.tile = {
    /**
     * @constant
     * @type {number}
     */
    size: 16,
    /**
     * @constant
     * @type {number}
     */
    halfSize: 8,
    /**
     * @constant
     * @type {number}
     */
    halfSizeSquared: 8 * 8,
    /**
     * @constant
     * @type {number}
     */
    threeQuarterSize: 12,
    /**
     * @constant
     * @type {number}
     */
    quarterSize: 4,
    /**
     * @constant
     * @type {number}
     */
    eighthSize: 2,
    /**
     * @constant
     * @type {number}
     */
    doubleSize: 32
};

/**
 * @const
 * @type {Object}
 */
Settings.skillGraph = {
    cellWidth: 260, // 220 250
    cellHeight: 110,
    connectorOffsetX: 100,
    connectorOffsetY: 40,
    minZoom: 20,  // <-- zooming out
    maxZoom: 100, // <-- zooming in
    zoomStep: 10,
    nodeViewWidth: 180 // <-- set in the css 'skill-tree-button-outer'
};


/**
 * @const
 * @type {Object}
 */
Settings.physics = {
    /**
     * @constant
     * @type {number}
     */
    gravityRatePerFrame: 4,
    /**
     * @constant
     * @type {number}
     */
    walkRatePerFrame: 2, //3
    /**
     * @constant
     * @type {number}
     */
    jumpRatePerFrame: 2
};


/**
 * @constant
 * @type {Object}
 */
Settings.character = {
    jumpPower: 2.5 * Settings.tile.size,
    maxJumpPixelsPerFrame: 2,
    halfWidth: 5
};


/**
 * @constant
 * @type {Object}
 */
Settings.combat = {
    critialHitMultiplier: 1.5
};

/**
 * @constant
 * @type {Object}
 */
Settings.input = {
    panning: {
        /**
         * The rate at which the motion decays when you drag the map and then let go.
         * @const
         * @type {number}
         */
        decayFactor: 0.95
    }
};


/**
 * @constant
 * @type {Object}
 */
Settings.projection = {
    /**
     * @constant
     * @type {number}
     */
    zoomInScaleLimit: 8, // 10.0,
    /**
     * The smaller the zoom scale, the further out we are zoomed.
     * @constant
     * @type {number}
     */
    zoomOutScaleLimit: 1.5, // 0.5
    /**
     * @constant
     * @type {number}
     */
    zoomDefault: 2.0,
    /**
     * @constant
     * @type {number}
     */
    zoomStep: 0.25, // 0.5
    /**
     * @constant
     * @type {number}
     */
    startCenterX: 0,
    /**
     * @constant
     * @type {number}
     */
    startCenterY: 0
};

/**
 * @constant
 * @type {Object}
 */
Settings.text = {
    /**
     * @constant
     * @type {number}
     */
    maxInfoTextCount: 7,
    /**
     * @constant
     * @type {number}
     */
    maxDamageTextCount: 4,
    /**
     * @constant
     * @type {number}
     */
    textFrameDuration: 45
}

/**
 * @constant
 * @type {Object}
 */
Settings.grid = {
    /**
     * @constant
     * @type {number}
     */
    numZoneColsInGrid : 3,  // 4
    /**
     * @constant
     * @type {number}
     */
    numZoneRowsInGrid: 3, // 4

    /**
     * @constant
     * @type {number}
     */
    numRegionColsInZone: 2,  // 2
    /**
     * @constant
     * @type {number}
     */
    numRegionRowsInZone: 2, // 2

    /**
     * @constant
     * @type {number}
     */
    numTileColsInRegion: 5, // 5
    /**
     * @constant
     * @type {number}
     */
    numTileRowsInRegion: 5  // 5
};

// Grid:  3 * 2 * 5 = 56 rows x 56 cols

// Grid  3 * 2 * 5 = 30 x 30 = 900 tiles per grid.

// 9 grids = 8000 total tiles.

////////////////////////////////////

/**
 * @constant
 * @type {number}
 */
Settings.grid.regionTileWidth = Settings.grid.numTileColsInRegion;
/**
 * @constant
 * @type {number}
 */
Settings.grid.regionTileHeight = Settings.grid.numTileRowsInRegion;

/**
 * @constant
 * @type {number}
 */
Settings.grid.zoneTileWidth = Settings.grid.numRegionColsInZone * Settings.grid.regionTileWidth;
/**
 * @constant
 * @type {number}
 */
Settings.grid.zoneTileHeight = Settings.grid.numRegionRowsInZone * Settings.grid.regionTileHeight;

/**
 * @constant
 * @type {number}
 */
Settings.grid.gridTileWidth = Settings.grid.numZoneColsInGrid * Settings.grid.zoneTileWidth;
/**
 * @constant
 * @type {number}
 */
Settings.grid.gridTileHeight = Settings.grid.numZoneRowsInGrid * Settings.grid.zoneTileHeight;


/**
 * @constant
 * @type {number}
 */
Settings.grid.regionPixelWidth = Settings.grid.regionTileWidth * Settings.tile.size;
/**
 * @constant
 * @type {number}
 */
Settings.grid.regionPixelHeight = Settings.grid.regionTileHeight * Settings.tile.size;

/**
 * @constant
 * @type {number}
 */
Settings.grid.zonePixelWidth = Settings.grid.zoneTileWidth * Settings.tile.size;
/**
 * @constant
 * @type {number}
 */
Settings.grid.zonePixelHeight = Settings.grid.zoneTileHeight * Settings.tile.size;

/**
 * @constant
 * @type {number}
 */
Settings.grid.gridPixelWidth = Settings.grid.gridTileWidth * Settings.tile.size;
/**
 * @constant
 * @type {number}
 */
Settings.grid.gridPixelHeight = Settings.grid.gridTileHeight * Settings.tile.size;


/**
 * @constant
 * @type {Object}
 */
Settings.start = {
    /**
     * Start x-coordinate of the character.
     * @constant
     * @type {number}
     */
    startX : 20000, // Middle of the world.
    /**
     * Start y-coordinate of the character.
     * @constant
     * @type {number}
     */
    startY: -100 * Settings.tile.size
};


/**
 * @constant
 * @type {Object}
 */
Settings.effect = {
    /**
     * How fast a travel effect moves per frame.
     * @constant
     * @type {number}
     */
    effectTravelPixlesPerFrame: 5.5,
    /**
     * How fast a FAST travel effect moves per frame.
     * @constant
     * @type {number}
     */
    fastEffectTravelPixelsPerFrame: 6.5,
    /**
     * How fast a SLOW travel effect moves per frame.
     * @constant
     * @type {number}
     */
    slowEffectTravelPixelsPerFrame: 3.0,
    /**
     * The length of time that the lightning effect is active.
     * @constant
     * @type {number}
     */
    lightningFrames: Settings.time.turnFrames * 2

};

/**
 * @constant
 * @type {Object}
 */
Settings.lighting = {
    lightSourceRadius: 5  
};

// /**
//  * @constant
//  * @type {Object}
//  */
// Settings.attackRange = {
//     /**
//      * @constant
//      * @type {number}
//      */
//     melee: Settings.tile.size + Settings.tile.size,
//     /**
//      * @constant
//      * @type {number}
//      */
//     meleeLong: Settings.tile.size + Settings.tile.size + Settings.tile.size,
//     /**
//      * @constant
//      * @type {number}
//      */
//     ranged: Settings.tile.size * 6,
//     /**
//      * @constant
//      * @type {number}
//      */
//     rangedReflection: Settings.tile.size * 6
// };

/**
 * @constant
 * @type {Object}
 */
Settings.skill = {
};

/**
 * @constant
 * @type {Object}
 */
Settings.bonus = {
    newBonusTurns: Settings.time.turnsPerSecond * (60 * 15),   // 15 minutes?
    activationTurns: Settings.time.turnsPerSecond * (60 * 5),
    activationCost: 1000
};

/**
 * @constant
 * @type {Object}
 */
Settings.prestige = {
    multiplierPerPrestige: 0.2,
    incrementBonusPerUpgrade: 0.2,
    baseBonusPerUpgrade: 0.2
}

/**
 * @constant
 * @type {Object}
 */
Settings.ruby = {
    multiplierPerRuby: 1,
    incrementBonusPerUpgrade: 0.2,
    baseBonusPerUpgrade: 0.2
}

/**
 * @constant
 * @type {Object}
 */
Settings.blocks = {
    multiplierPerBlock: 0.0,
    incrementBonusPerUpgrade: 0.01,
    baseBonusPerUpgrade: 0.01
}


/**
 * @constant
 * @type {Object}
 */
Settings.depth = {
    multiplierPerDepth: 0.025,
    incrementBonusPerUpgrade: 0.025,
    baseBonusPerUpgrade: 0.025
}

Settings.minedDamage = {
    incrementBonusPerUpgrade: 1,
    baseBonusPerUpgrade: 1
}

/**
 * @constant
 * @type {Object}
 */
Settings.gold = {
    upgradeBaseCost: 25000,
    upgradeBaseCostAdjustment: 25000,
    damageMultiplierPerUpgrade: 10,
    oreBonusMultiplierPerUpgrade: 10,
}

/**
 * @constant
 * @type {Object}
 */
Settings.skill.costs = {
    addDrill1: 0,
    addOrbit1: 1,
    addMissile1: 2,
    addLaser1: 3,
    
    addMiner1: 5,
    addMiner2: 20,
    addMiner3: 40,
    addMiner4: 60,
    increment: 2
}
Settings.skill.fissure = {
    delayMillis: 2500,
    forkChance: 75,
    secondForkChance: 85,
    minBlocks: 3,
    blocksPerUpgrade: 2
};
Settings.skill.lightning = {
    minRange: 3,
    delayMillis: 2500,
    forkChance: 75,
    secondForkChance: 85,
    minBlocks: 3,
    blocksPerUpgrade: 1,
    tilesPerUpgrade: 1
};
Settings.skill.chain = {
    minRange: 3,
    minSize: 3,
    delayMillis: 2500,
    blocksPerUpgrade: 1,
    tilesPerUpgrade: 1
};
Settings.skill.laser = {
    minRange: 3,
    tilesPerUpgrade: 1
};
Settings.skill.drill = {
    minRange: 3,
    tilesPerUpgrade: 1
};
Settings.skill.missiles = {
    initialMissiles: 5,
    missilesPerUpgrade: 3
};
Settings.skill.orbit = {
    minRangePixels: 2 * Settings.tile.size,
    rangePixelsPerUpgrade: Settings.tile.halfSize
};
Settings.skill.bonus = {
    oreBonusMultiplierPerUpgrade: 10,
    additiveUpgradeBonusMultiplerPerUpgrade: 2,
    multiplicativeUpgradeBonusMultiplerPerUpgrade: 10,
    minedOreBaseTiles: 8,
    minedOreBonusPerUpgrade: 1,
    fallSpeedMuliplierPerUgrade: 0.15
};
Settings.skill.common = {
    damagePercentPerUpgrade: 25,
    damagePerUpgrade: 0.25,
    baseActivationTurns: Settings.time.turnsPerSecond * 10,
    baseCoolDownTurns: Settings.time.turnsPerSecond * 60,
    activationSecondsPerUpgrade: 3,
    coolDownSecondsPerUpgrade: 5,
    activationTurnsPerUpgrade: Settings.time.turnsPerSecond * 3,
    coolDownTurnsPerUpgrade: Settings.time.turnsPerSecond * 5
};

/**
 * @constant
 * @type {Object}
 */
Settings.automation = {
    minDelayTurns: Settings.time.turnsPerSecond * 1,
    baseUpgradeDelayTurns: Settings.time.turnsPerSecond * 61,    // 61 seconds
    turnsPerRateUpgrade: Settings.time.turnsPerSecond * 5,       // 5 seconds per upgrade
    prestigeUpgradeDelayTurns: Settings.time.turnsPerSecond * (60 * 61), // 61 minutes
    prestigeTurnsPerRateUpgrade: Settings.time.turnsPerSecond * (60 * 5), // 5 minutes
    //prestigeMinTurnsSinceLastChange: Settings.time.turnsPerSecond * (60 * 5), // 5 minutes
    secondsPerRateUpgrade: 5,
    minutesPerPrestigeRateUpgrade: 5
}

/**
 * @constant
 * @type {Object}
 */
Settings.offline = {
    defaultOfflineMinutes: 30,
    defaultOfflineTurns: (30 * 60 * Settings.time.turnsPerSecond),
    minutesPerUpgrade: 30,
    turnsPerUpgrade: 30 * (60 * Settings.time.turnsPerSecond)
};